import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AINormalInput from '../../ui/inputs/AINormalInput';
import MainSquareButton from '../../ui/buttons/MainSquareButton';
import create_prompt_schedule_api_call from '../../lib/api/prompt-schedules/create-prompt-schedule';
import update_prompt_schedule_api_call from '../../lib/api/prompt-schedules/update-prompt-schedule';
import store from '../../store/store';
import { setMessage } from '../../store/reducers/appSlice';
import { saveSVG, crossSVG } from '../../lib/allCustomSVGs';
import '../../styles/pages/MCP/mcpConfigBox.scss';
import '../../styles/pages/MCP/requestMCP.scss';
import '../../styles/pages/skills/createSkillModal.scss';
import '../../styles/pages/prompt-schedules/promptScheduleModal.scss';
import type {
  PromptSchedule,
  PromptScheduleType,
} from '../../lib/api/prompt-schedules/types';

const WEEKDAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;

const WEEKDAY_ABBR: Record<typeof WEEKDAYS[number], string> = {
  Monday: 'Mon',
  Tuesday: 'Tue',
  Wednesday: 'Wed',
  Thursday: 'Thu',
  Friday: 'Fri',
  Saturday: 'Sat',
  Sunday: 'Sun',
};

const MONTH_DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

function parseTimeForInput(apiTime: string): string {
  if (!apiTime) return '09:00';
  const parts = apiTime.split(':');
  if (parts.length >= 2) {
    return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
  }
  return '09:00';
}

interface PromptScheduleFormModalProps {
  mode: 'create' | 'edit';
  schedule?: PromptSchedule | null;
  onClose: () => void;
  onSuccess: () => void;
}

const PromptScheduleFormModal = ({
  mode,
  schedule,
  onClose,
  onSuccess,
}: PromptScheduleFormModalProps) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [prompt, setPrompt] = useState('');
  const [scheduleType, setScheduleType] =
    useState<PromptScheduleType>('weekly');
  const [scheduleTime, setScheduleTime] = useState('09:00');
  const [timezone, setTimezone] = useState('UTC');
  const [weeklyDays, setWeeklyDays] = useState<string[]>([]);
  const [monthlyDays, setMonthlyDays] = useState<number[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (mode === 'edit' && schedule) {
      setName(schedule.name ?? '');
      setPrompt(schedule.prompt ?? '');
      setScheduleType(schedule.schedule_type ?? 'weekly');
      setScheduleTime(parseTimeForInput(schedule.schedule_time));
      setTimezone(schedule.timezone?.trim() ?? 'UTC');
      const rawDays = (schedule.schedule_days ?? []) as (string | number)[];
      if (schedule.schedule_type === 'weekly') {
        const stringDays = rawDays.filter(
          (d): d is string => typeof d === 'string'
        );
        setWeeklyDays(stringDays);
      } else {
        setWeeklyDays([]);
      }
      if (schedule.schedule_type === 'monthly') {
        const numDays = rawDays
          .filter((d): d is number => typeof d === 'number')
          .filter((n) => n >= 1 && n <= 31);
        setMonthlyDays(Array.from(new Set(numDays)).sort((a, b) => a - b));
      } else {
        setMonthlyDays([]);
      }
    } else {
      setName('');
      setPrompt('');
      setScheduleType('weekly');
      setScheduleTime('09:00');
      setTimezone('UTC');
      setWeeklyDays([]);
      setMonthlyDays([]);
    }
  }, [mode, schedule?.id]);

  const toggleWeekday = (day: string) => {
    setWeeklyDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const toggleMonthDay = (day: number) => {
    setMonthlyDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const clearForm = () => {
    setName('');
    setPrompt('');
    setScheduleType('weekly');
    setScheduleTime('09:00');
    setTimezone('UTC');
    setWeeklyDays([]);
    setMonthlyDays([]);
  };

  const buildScheduleDays = (): (string | number)[] => {
    if (scheduleType === 'weekly') return weeklyDays;
    if (scheduleType === 'monthly') {
      return [...monthlyDays].sort((a, b) => a - b);
    }
    return [];
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (!name.trim()) {
      store.dispatch(
        setMessage({
          message: t('PromptScheduleNameRequired'),
          messageType: 'warning',
        })
      );
      return;
    }
    if (!prompt.trim()) {
      store.dispatch(
        setMessage({
          message: t('PromptSchedulePromptRequired'),
          messageType: 'warning',
        })
      );
      return;
    }
    if (!scheduleTime || !String(scheduleTime).trim()) {
      store.dispatch(
        setMessage({
          message: t('PromptScheduleTimeRequired'),
          messageType: 'warning',
        })
      );
      return;
    }
    if (!timezone.trim()) {
      store.dispatch(
        setMessage({
          message: t('PromptScheduleTimezoneRequired'),
          messageType: 'warning',
        })
      );
      return;
    }

    const schedule_days = buildScheduleDays();
    if (scheduleType === 'weekly' && schedule_days.length === 0) {
      store.dispatch(
        setMessage({
          message: t('PromptScheduleWeeklyDaysRequired'),
          messageType: 'warning',
        })
      );
      return;
    }
    if (scheduleType === 'monthly' && schedule_days.length === 0) {
      store.dispatch(
        setMessage({
          message: t('PromptScheduleMonthlyDaysRequired'),
          messageType: 'warning',
        })
      );
      return;
    }

    const is_active = mode === 'create' ? true : Boolean(schedule?.is_active);

    const payload = {
      name: name.trim(),
      prompt: prompt.trim(),
      schedule_type: scheduleType,
      schedule_time: scheduleTime,
      timezone: timezone.trim(),
      schedule_days,
      is_active,
    };

    setIsSubmitting(true);
    try {
      if (mode === 'edit' && schedule?.id) {
        const res = await update_prompt_schedule_api_call(schedule.id, payload);
        if (res?.success) {
          store.dispatch(
            setMessage({
              message: res?.message || t('PromptScheduleUpdatedSuccess'),
              messageType: 'success',
            })
          );
          onSuccess();
        } else {
          store.dispatch(
            setMessage({
              message: res?.message || t('SomethingWentWrong-trying-again-msg'),
              messageType: 'error',
            })
          );
        }
      } else {
        const res = await create_prompt_schedule_api_call(payload);
        if (res?.success) {
          store.dispatch(
            setMessage({
              message: res?.message || t('PromptScheduleCreatedSuccess'),
              messageType: 'success',
            })
          );
          onSuccess();
        } else {
          store.dispatch(
            setMessage({
              message: res?.message || t('SomethingWentWrong-trying-again-msg'),
              messageType: 'error',
            })
          );
        }
      }
    } catch {
      store.dispatch(
        setMessage({
          message: t('SomethingWentWrong-trying-again-msg'),
          messageType: 'error',
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const formId =
    mode === 'edit'
      ? 'edit-prompt-schedule-form'
      : 'create-prompt-schedule-form';

  return (
    <form
      id={formId}
      onSubmit={handleSubmit}
      className='mcpConfigBox createSkillModal promptScheduleModal'
    >
      <div className='requestMCP__titles'>
        <h3>
          {mode === 'edit'
            ? t('EditPromptSchedule')
            : t('CreatePromptSchedule')}
        </h3>
        <span>{t('PromptScheduleFormSubtitle')}</span>
      </div>

      <div className='mcpConfigBox__content'>
        <div className='mcpConfigBox__content--inputs'>
          <div
            className='mcpConfigBox__content--inputs-field createSkillModal__nameField'
            style={{ marginBottom: '1rem' }}
          >
            <AINormalInput
              id={`${formId}-name`}
              value={name}
              required
              changeListeners={[(e) => setName(e.target.value)]}
              placeholder={t('PromptScheduleName')}
              innerPlaceholder={t('PromptScheduleNamePlaceholder')}
            />
          </div>

          <div
            className='mcpConfigBox__content--inputs-field'
            style={{ marginBottom: '1rem' }}
          >
            <label
              htmlFor={`${formId}-prompt`}
              className='createSkillModal__textareaLabel createSkillModal__requiredLabel'
            >
              {t('PromptSchedulePromptLabel')}
            </label>
            <textarea
              id={`${formId}-prompt`}
              className='createSkillModal__textarea'
              value={prompt}
              required
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={t('PromptSchedulePromptPlaceholder')}
              rows={5}
            />
          </div>

          <div
            className='mcpConfigBox__content--inputs-field promptScheduleModal__field'
            style={{ marginBottom: '1rem' }}
          >
            <label
              className='createSkillModal__textareaLabel createSkillModal__requiredLabel'
              htmlFor={`${formId}-type`}
            >
              {t('PromptScheduleType')}
            </label>
            <select
              id={`${formId}-type`}
              className='promptScheduleModal__select promptScheduleModal__select--full'
              value={scheduleType}
              onChange={(e) =>
                setScheduleType(e.target.value as PromptScheduleType)
              }
              required
            >
              <option value='daily'>{t('PromptScheduleTypeDaily')}</option>
              <option value='weekly'>{t('PromptScheduleTypeWeekly')}</option>
              <option value='monthly'>{t('PromptScheduleTypeMonthly')}</option>
            </select>
          </div>

          <div
            className='mcpConfigBox__content--inputs-field promptScheduleModal__fieldRow promptScheduleModal__fieldRow--aligned'
            style={{ marginBottom: '1rem' }}
          >
            <div className='promptScheduleModal__fieldRowItem promptScheduleModal__fieldRowItem--time'>
              <div className='createSkillModal__nameField'>
                <div className='aiNormalInput-outer'>
                  <label htmlFor={`${formId}-time`}>
                    {t('PromptScheduleTime')}
                  </label>
                  <div className='aiNormalInput'>
                    <input
                      id={`${formId}-time`}
                      type='time'
                      className='promptScheduleModal__timeNative'
                      value={scheduleTime}
                      onChange={(e) => setScheduleTime(e.target.value)}
                      required
                    />
                  </div>
                </div>
              </div>
            </div>
            {/* 
            <div className='promptScheduleModal__fieldRowItem promptScheduleModal__fieldRowItem--grow'>
              <div className='createSkillModal__nameField'>
                <AINormalInput
                  id={`${formId}-tz`}
                  value={timezone}
                  required
                  changeListeners={[(e) => setTimezone(e.target.value)]}
                  placeholder={t('PromptScheduleTimezone')}
                  innerPlaceholder={t('PromptScheduleTimezonePlaceholder')}
                />
              </div>
            </div>
             */}
          </div>

          {scheduleType === 'weekly' && (
            <div
              className='mcpConfigBox__content--inputs-field'
              style={{ marginBottom: '1rem' }}
            >
              <span className='createSkillModal__textareaLabel createSkillModal__requiredLabel'>
                {t('PromptScheduleWeekdays')}
              </span>
              <div className='promptScheduleModal__weekdayBoxes' role='group'>
                {WEEKDAYS.map((day) => (
                  <label
                    key={day}
                    className={`promptScheduleModal__weekdayBox${
                      weeklyDays.includes(day)
                        ? ' promptScheduleModal__weekdayBox--checked'
                        : ''
                    }`}
                  >
                    <input
                      type='checkbox'
                      checked={weeklyDays.includes(day)}
                      onChange={() => toggleWeekday(day)}
                      aria-label={day}
                    />
                    <span className='promptScheduleModal__weekdayBox-label'>
                      {WEEKDAY_ABBR[day]}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {scheduleType === 'monthly' && (
            <div
              className='mcpConfigBox__content--inputs-field'
              style={{ marginBottom: '1rem' }}
            >
              <span className='createSkillModal__textareaLabel createSkillModal__requiredLabel'>
                {t('PromptScheduleMonthlyDays')}
              </span>
              <div
                className='promptScheduleModal__monthDayBoxes'
                role='group'
                aria-label={t('PromptScheduleMonthlyDays')}
              >
                {MONTH_DAYS.map((day) => (
                  <label
                    key={day}
                    className={`promptScheduleModal__monthDayBox${
                      monthlyDays.includes(day)
                        ? ' promptScheduleModal__monthDayBox--checked'
                        : ''
                    }`}
                  >
                    <input
                      type='checkbox'
                      checked={monthlyDays.includes(day)}
                      onChange={() => toggleMonthDay(day)}
                      aria-label={t('PromptScheduleMonthDayAria', { day })}
                    />
                    <span className='promptScheduleModal__monthDayBox-label'>
                      {day}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className='mcpConfigBox__buttons createSkillModal__buttons'>
        {mode === 'create' && (
          <MainSquareButton
            type='button'
            value={t('Clear')}
            onClick={clearForm}
            disabled={isSubmitting}
            classes='createSkillModal__buttons--clear'
            icon={crossSVG}
            normal={true}
            buttonType='white'
          />
        )}
        <MainSquareButton
          value={
            isSubmitting
              ? (mode === 'edit' ? t('Updating') : t('Creating')) + '...'
              : mode === 'edit'
              ? t('SaveChanges')
              : t('CreatePromptSchedule')
          }
          disabled={isSubmitting}
          type='submit'
          form={formId}
          classes='mcpConfigBox__buttons--update createSkillModal__buttons--submit'
          icon={saveSVG}
        />
      </div>
    </form>
  );
};

export default PromptScheduleFormModal;
